import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpHeaders } from '@angular/common/http';
import { ApiService } from '@core/api/api.service';
import { User } from '@core/models/user.model';
import { UserProfile } from '@core/models/user-profile.model';

import { AppStoreQuery } from '@core/state';
import { AppStoreService } from '@core/state/app-state.service';
import { resetStores } from '@datorama/akita';
import { deserialize } from '@junte/serialize-ts';
import { loginAbsoluteRoute } from '@shared/shared-routes';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';

import { UserProfileDTO } from '@core/dto/user-profile.dto';
import { LanguageService } from '@core/language/language.service';
import { SubscriptionService } from '@core/subscription/subscription.service';
import { RegisterUserRequest } from '@feature/user/register/register-request';
import { clearStoredTemplateClientContextClientId } from '@core/template-dd/template-client-context.storage';
import { AuthenticationResponse, AuthenticationToken } from './authentication-token';
import { AuthenticationTokenRequest, PasswordResetRequest } from './authentication-token-request';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private readonly LOGOUT_REASON_KEY = 'wai_logout_reason';
  private readonly DEVICE_ID_KEY = 'wai_device_id';
  private readonly ACTIVE_THEME_KEY = 'activeTheme';
  private readonly INITIAL_THEME_KEY = 'initialThemeParams';
  private readonly SESSION_CONTEXT_LOCAL_STORAGE_KEYS = [
    'keys',
    'redirectUrl',
    'userType',
    'isStaff',
    'isAnalystUser',
    'uid',
    'selectedClients',
    'selectedTabIndex',
    'information',
    'AppStore',
    'organizations',
    'AkitaStores',
  ];
  // Set to track tokens that have already been used
  private usedTokens = new Set<string>();

  constructor(
    private api: ApiService,
    private router: Router,
    private store: AppStoreService,
    private appStoreQuery: AppStoreQuery,
    private languageService: LanguageService,
    private subscriptionService: SubscriptionService,
  ) {}

  private getDeviceId(): string {
    let deviceId = localStorage.getItem(this.DEVICE_ID_KEY);
    if (!deviceId) {
      deviceId = uuidv4();
      localStorage.setItem(this.DEVICE_ID_KEY, deviceId);
    }
    return deviceId;
  }

  public authenticate(credentials: AuthenticationTokenRequest): Observable<AuthenticationResponse> {
    const requestAuthentication = {
      ...credentials,
      deviceId: this.getDeviceId(),
    };

    return this.api.post<AuthenticationResponse>(`accounts/login`, requestAuthentication).pipe(
      tap((auth) => {
        this.store.updateAuthentication({ expiry: auth.expiry, token: auth.token });
        this.store.updateUser(deserialize(auth.user, User));
        this.persistUserSessionFields(auth.user);
      }),
      switchMap((authResponse) =>
        this.api.get<UserProfileDTO>('accounts/profile').pipe(
          tap((profileResp) => {
            this.syncSelectedPartnerFromProfile(profileResp);
            this.subscriptionService.syncFromProfileResponse(profileResp);
          }),
          map(() => authResponse),
        ),
      ),
    );
  }

  public register(data: RegisterUserRequest) {
    const _data = {
      ...data,
      deviceId: this.getDeviceId(),
    };
    return this.api.post('accounts/register', _data);
  }

  public unauthenticate(reason: 'manual' | 'idle' = 'manual'): void {
    this.subscriptionService.stopActivationPolling();
    this.languageService.handleLogout();

    // Used by interceptors to decide which UX to show if 401s happen during logout.
    localStorage.setItem(this.LOGOUT_REASON_KEY, reason);

    // Clear user-scoped persisted context before resetting stores to prevent
    // stale organization/session data from leaking into the next login.
    this.clearSessionScopedStorage();

    // Optimistically clear auth state immediately so other services/components
    // do not race with server token invalidation.
    const token = this.appStoreQuery.getAuthenticationToken();
    localStorage.removeItem('uid');
    localStorage.removeItem('userType');
    localStorage.removeItem('isStaff');
    localStorage.removeItem('isAnalystUser');
    clearStoredTemplateClientContextClientId();
    this.store.reset();
    this.resetAllStores();

    const headers = token ? new HttpHeaders().set('X-API-Token', token) : undefined;
    const options = headers ? { headers } : undefined;

    this.api
      .post(`accounts/logout`, {}, options)
      .pipe(take(1))
      .subscribe({
        next: () => {
          localStorage.removeItem(this.LOGOUT_REASON_KEY);
          this.router.navigate([loginAbsoluteRoute]);
        },
        error: (error) => {
          console.warn('Logout API call failed, but local state is already cleared:', error);
          localStorage.removeItem(this.LOGOUT_REASON_KEY);
          this.router.navigate([loginAbsoluteRoute]);
        },
      });
  }

  private clearSessionScopedStorage(): void {
    this.SESSION_CONTEXT_LOCAL_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));

    // Akita persist plugin commonly stores snapshots under keys containing
    // "akita". Remove them defensively so user switches always start clean.
    for (let index = localStorage.length - 1; index >= 0; index -= 1) {
      const key = localStorage.key(index);
      if (key && key.toLowerCase().includes('akita')) {
        localStorage.removeItem(key);
      }
    }

    sessionStorage.clear();
  }

  private resetAllStores(): void {
    resetStores();
  }

  public resetPassword(payload: PasswordResetRequest): Observable<AuthenticationResponse> {
    // Check if this token has already been used
    const tokenKey = `${payload.user_id}:${payload.token}`;
    if (this.usedTokens.has(tokenKey)) {
      // Return a successful response without making the API call
      return this.appStoreQuery.selectAuthentication().pipe(
        take(1),
        map((auth): AuthenticationResponse => {
          const currentUser = this.appStoreQuery.getValue().user as any;
          this.persistUserSessionFields(currentUser);
          return {
            expiry: auth?.expiry || '',
            token: auth?.token || '',
            user: currentUser, // Cast to satisfy type requirements
          };
        }),
        catchError(() => {
          // If we can't get the current auth state, navigate to login
          this.router.navigate([loginAbsoluteRoute]);
          return of(null as any); // Cast to satisfy type requirements
        }),
      );
    }

    // Add the token to the used set before making the request
    this.usedTokens.add(tokenKey);

    return this.api.post<AuthenticationResponse>('accounts/users/password', payload).pipe(
      tap((auth) => {
        this.store.updateAuthentication({ expiry: auth.expiry, token: auth.token });
        this.store.updateUser(deserialize(auth.user, User));
        this.persistUserSessionFields(auth.user);
      }),
      catchError((error) => {
        // If the request fails, remove the token from the used set
        this.usedTokens.delete(tokenKey);
        throw error;
      }),
    );
  }

  public isAuthExpired(): Observable<AuthenticationToken> {
    return this.appStoreQuery.selectAuthentication().pipe(map((auth: AuthenticationToken) => auth));
  }

  public isAuthenticated(): Observable<boolean> {
    return this.appStoreQuery.selectAuthentication().pipe(
      map((auth: AuthenticationToken) => Boolean(auth?.token)),
      // TODO: check if token is valid based on expiration time and add refreshing
      // https://ownlyde.atlassian.net/browse/WAI-90
    );
  }

  public isTwoFacEnabled(): Observable<boolean> {
    return this.appStoreQuery.selectUserProfile().pipe(
      map((user: UserProfile) => {
        // Return true if either 2FA or SMS OTP is enabled
        return Boolean(user?.isTwoFaEnabled || user?.isSmsOtpEnabled);
      }),
    );
  }

  private syncSelectedPartnerFromProfile(profile: UserProfileDTO): void {
    const assignedPartners = Array.isArray((profile as any)?.partnerAdminPartners)
      ? ((profile as any).partnerAdminPartners as Array<{ uid?: string }>)
      : [];
    const profilePartnerUid = (profile as any)?.partnerAdmin?.uid as string | undefined;
    const assignedPartnerUids = assignedPartners.map((item) => item?.uid).filter((uid): uid is string => Boolean(uid));

    if (assignedPartnerUids.length === 0 && !profilePartnerUid) {
      return;
    }

    const activeTheme = this.parseStoredJson<{ uid?: string }>(this.ACTIVE_THEME_KEY);
    const initialTheme = this.parseStoredJson<{ uid?: string }>(this.INITIAL_THEME_KEY);
    const initialThemeUid = initialTheme?.uid;
    const activeUid = activeTheme?.uid;
    const isProfileUidAllowed = profilePartnerUid
      ? assignedPartnerUids.length === 0 || assignedPartnerUids.includes(profilePartnerUid)
      : false;
    const fallbackUid =
      (isProfileUidAllowed && profilePartnerUid) ||
      (initialThemeUid && assignedPartnerUids.includes(initialThemeUid) && initialThemeUid) ||
      (activeUid && assignedPartnerUids.includes(activeUid) && activeUid) ||
      profilePartnerUid ||
      assignedPartnerUids[0];

    // Keep both storages aligned after partner reassignment.
    // Interceptors use both keys for partner headers, so stale domain uid can break first-login calls.
    localStorage.setItem(this.ACTIVE_THEME_KEY, JSON.stringify({ ...(activeTheme ?? {}), uid: fallbackUid }));
    localStorage.setItem(this.INITIAL_THEME_KEY, JSON.stringify({ ...(initialTheme ?? {}), uid: fallbackUid }));
  }

  private parseStoredJson<T>(storageKey: string): T | null {
    const rawValue = localStorage.getItem(storageKey);
    if (!rawValue) {
      return null;
    }

    try {
      return JSON.parse(rawValue) as T;
    } catch {
      return null;
    }
  }

  private persistUserSessionFields(user: {
    uid?: string | null;
    id?: string | null;
    userType?: string | null;
    user_type?: string | null;
    isStaff?: boolean | null;
    isAnalystUser?: boolean | null;
  } | null | undefined): void {
    const resolvedUid = String(user?.uid ?? user?.id ?? '').trim();
    const resolvedUserType = String(user?.userType ?? user?.user_type ?? '').trim();

    if (resolvedUserType) {
      localStorage.setItem('userType', resolvedUserType);
    }

    if (user?.isStaff != null) {
      localStorage.setItem('isStaff', String(user.isStaff));
    }

    if (user?.isAnalystUser != null) {
      localStorage.setItem('isAnalystUser', String(user.isAnalystUser));
    }

    if (resolvedUid) {
      localStorage.setItem('uid', resolvedUid);
    }
  }
}
